import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { CmsProject, ProjectStatus } from '../../types/cms';

const GOLD = '#9d6f32';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (newProject: CmsProject) => void;
  existingSlugs: string[];
}

export const NewProjectModal: React.FC<NewProjectModalProps> = ({
  open,
  onClose,
  onCreate,
  existingSlugs,
}) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tagline, setTagline] = useState('');
  const [microMarket, setMicroMarket] = useState('Vanakla');
  const [status, setStatus] = useState<ProjectStatus>('under-construction');
  const [completionDate, setCompletionDate] = useState('December 2026');
  const [reraNumber, setReraNumber] = useState('');
  const [typologyPreset, setTypologyPreset] = useState('2bhk_3bhk');

  const handleNameChange = (val: string) => {
    setName(val);
    const autoSlug = val
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setSlug(autoSlug);
  };

  const isSlugValid = slug && !existingSlugs.includes(slug);

  const handleSubmit = () => {
    if (!name.trim() || !isSlugValid) return;

    const baseConfigurations =
      typologyPreset === '2bhk_3bhk'
        ? [
            { id: `c_${Date.now()}_1`, type: '2 BHK Luxury', area: '1,150 – 1,280 sq.ft', price: '₹48 – ₹55 Lakhs', units: 48, availabilityStatus: 'Available' as const },
            { id: `c_${Date.now()}_2`, type: '3 BHK Grande', area: '1,650 – 1,850 sq.ft', price: '₹72 – ₹85 Lakhs', units: 24, availabilityStatus: 'Available' as const },
          ]
        : typologyPreset === 'ready_shops'
        ? [
            { id: `c_${Date.now()}_1`, type: '2 BHK Ready Flat', area: '1,100 sq.ft', price: '₹45 – ₹50 Lakhs', units: 60, availabilityStatus: 'Available' as const },
            { id: `c_${Date.now()}_2`, type: 'Ground Floor Commercial Shop', area: '450 sq.ft', price: '₹70 – ₹90 Lakhs', units: 12, availabilityStatus: 'Available' as const },
          ]
        : [
            { id: `c_${Date.now()}_1`, type: '2 BHK Premium', area: '1,050 sq.ft', price: '₹42 – ₹46 Lakhs', units: 40, availabilityStatus: 'Available' as const },
          ];

    const project: CmsProject = {
      id: slug,
      slug,
      name: name.trim(),
      tagline: tagline.trim() || 'Excellence in Surat Real Estate',
      description: `${name.trim()} is an upcoming landmark development in ${microMarket}, Surat by Swarna Group. Designed with modern architecture, premium amenities, and world-class living standards.`,
      shortDescription: `Premium residences in ${microMarket}, Surat with modern lifestyle amenities by Swarna Group.`,
      location: `${microMarket}, Surat — 395009`,
      city: 'Surat',
      microMarket,
      status,
      completionDate,
      heroImage: 'https://picsum.photos/seed/swarna-new-hero/1920/1080',
      thumbnailImage: 'https://picsum.photos/seed/swarna-new-thumb/800/1000',
      totalFloors: 14,
      totalUnits: 80,
      reraNumber: reraNumber || 'Under RERA Registration',
      mapEmbedUrl: '',
      whatsappNumber: '+919876543210',
      configurations: baseConfigurations,
      highlights: [
        `Prime strategic location in ${microMarket}, Surat`,
        'Landscaped gardens and children play area',
        'State-of-art security & CCTV surveillance',
        'Attractive construction-linked payment plans',
      ],
      amenities: [
        { id: `a_${Date.now()}_1`, icon: 'Buildings', label: 'Grand Society Clubhouse', category: 'convenience' },
        { id: `a_${Date.now()}_2`, icon: 'Barbell', label: 'Modern Fitness Center', category: 'wellness' },
        { id: `a_${Date.now()}_3`, icon: 'PlayCircle', label: "Children's Play Area", category: 'wellness' },
        { id: `a_${Date.now()}_4`, icon: 'ShieldCheck', label: '24/7 Security & CCTV', category: 'safety' },
        { id: `a_${Date.now()}_5`, icon: 'Car', label: 'Reserved Covered Parking', category: 'convenience' },
      ],
      specifications: [
        {
          id: `s_${Date.now()}_1`,
          heading: 'Structure & Flooring',
          items: [
            { label: 'Structure', value: 'RCC framed structure, earthquake resistant compliant' },
            { label: 'Flooring', value: 'Vitrified tiles in all bedrooms, living, and kitchen' },
          ],
        },
      ],
      gallery: [],
      documents: [],
      faqs: [
        {
          id: `f_${Date.now()}_1`,
          question: `Where is ${name.trim()} located?`,
          answer: `${name.trim()} is located in ${microMarket}, Surat, offering easy connectivity across major Surat hubs.`,
          order: 1,
        },
        {
          id: `f_${Date.now()}_2`,
          question: `What is the expected possession timeline?`,
          answer: `The expected completion timeline is ${completionDate}.`,
          order: 2,
        },
      ],
      seo: {
        metaTitle: `${name.trim()} | Luxury Apartments in ${microMarket} Surat | Swarna Group`,
        metaDescription: `Explore ${name.trim()} in ${microMarket} Surat. Premium residential units with world-class amenities. Schedule a site visit today.`,
        targetKeywords: [`flats in ${microMarket.toLowerCase()} surat`, `${name.trim().toLowerCase()} surat`, 'swarna group projects'],
        canonicalUrl: `https://www.swarnagroup.in/projects/${slug}`,
        ogTitle: `${name.trim()} — Swarna Group Surat`,
        ogDescription: `Premium residences in ${microMarket}, Surat.`,
        schemaType: 'RealEstateListing',
      },
      isPublished: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      updatedBy: 'Admin',
    };

    onCreate(project);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <BusinessIcon sx={{ color: GOLD }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Create New Real Estate Project
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
          Add a new development to your portfolio. It will automatically generate its own live Next.js route on <code>swarnagroup.in</code> when published.
        </Typography>

        <TextField
          label="Project Name *"
          placeholder="e.g. Swarna Signature"
          fullWidth
          size="small"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
        />

        <TextField
          label="URL Slug (Auto-generated) *"
          helperText={
            !slug
              ? 'Used in website URL: swarnagroup.in/projects/[slug]'
              : existingSlugs.includes(slug)
              ? '⚠️ A project with this URL slug already exists!'
              : `Live URL will be: swarnagroup.in/projects/${slug}`
          }
          error={Boolean(slug && existingSlugs.includes(slug))}
          fullWidth
          size="small"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().trim().replace(/[^a-z0-9-]/g, ''))}
        />

        <TextField
          label="Tagline"
          placeholder="e.g. Luxury Living Redefined"
          fullWidth
          size="small"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Micro-Market (Surat)</InputLabel>
            <Select
              label="Micro-Market (Surat)"
              value={microMarket}
              onChange={(e) => setMicroMarket(e.target.value)}
            >
              <MenuItem value="Vanakla">Vanakla</MenuItem>
              <MenuItem value="Palanpur Canal Road">Palanpur Canal Road</MenuItem>
              <MenuItem value="Jahangirpura">Jahangirpura</MenuItem>
              <MenuItem value="Jahangirabad">Jahangirabad</MenuItem>
              <MenuItem value="Dandi Road">Dandi Road</MenuItem>
              <MenuItem value="Pal">Pal</MenuItem>
              <MenuItem value="Adajan">Adajan</MenuItem>
              <MenuItem value="Vesu">Vesu</MenuItem>
              <MenuItem value="VIP Road">VIP Road</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Construction Status</InputLabel>
            <Select
              label="Construction Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ProjectStatus)}
            >
              <MenuItem value="under-construction">Under Construction</MenuItem>
              <MenuItem value="ready-possession">Ready Possession</MenuItem>
              <MenuItem value="upcoming">Upcoming / New Launch</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Possession Date"
            placeholder="e.g. December 2026 or Ready Possession"
            fullWidth
            size="small"
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Typology Template</InputLabel>
            <Select
              label="Typology Template"
              value={typologyPreset}
              onChange={(e) => setTypologyPreset(e.target.value)}
            >
              <MenuItem value="2bhk_3bhk">2 BHK & 3 BHK Apartments</MenuItem>
              <MenuItem value="ready_shops">2 BHK Flats + Commercial Shops</MenuItem>
              <MenuItem value="2bhk_only">2 BHK Smart Residences</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="RERA Registration Number"
          placeholder="e.g. PR/GJ/SURAT/..."
          fullWidth
          size="small"
          value={reraNumber}
          onChange={(e) => setReraNumber(e.target.value)}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: '#6b6b6b' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || !isSlugValid}
          sx={{
            bgcolor: GOLD,
            color: '#fff',
            fontWeight: 700,
            '&:hover': { bgcolor: '#865e2b' },
          }}
        >
          Create Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};
